import { useState } from 'react';
import { COLORS } from '@regent/shared';
import { cn } from '@/lib/cn';
import { Tooltip } from './overlays';

const SIZES = {
  xs: 'size-5 text-[9px]',
  sm: 'size-6 text-2xs',
  md: 'size-7 text-xs',
  lg: 'size-9 text-sm',
  xl: 'size-14 text-lg',
} as const;

export type AvatarSize = keyof typeof SIZES;

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts.at(-1)![0]!).toUpperCase();
}

/** Stable color for a user or any id, picked from the shared palette. */
export function colorFor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return COLORS[Math.abs(hash) % COLORS.length]!;
}

export interface AvatarProps {
  name: string;
  image?: string | null;
  /** Seed for the fallback color; defaults to the name. */
  id?: string;
  size?: AvatarSize;
  online?: boolean;
  className?: string;
  title?: boolean;
}

export function Avatar({ name, image, id, size = 'md', online, className, title }: AvatarProps) {
  const [failed, setFailed] = useState(false);
  const body = (
    <span className={cn('relative inline-flex shrink-0', className)}>
      {image && !failed ? (
        <img
          src={image}
          alt={name}
          onError={() => setFailed(true)}
          className={cn('rounded-full object-cover', SIZES[size])}
        />
      ) : (
        <span
          aria-label={name}
          className={cn(
            'inline-flex items-center justify-center rounded-full font-semibold text-white select-none',
            SIZES[size],
          )}
          style={{ backgroundColor: colorFor(id ?? name) }}
        >
          {initials(name)}
        </span>
      )}
      {online ? (
        <span
          aria-label="Online"
          className="absolute -right-0.5 -bottom-0.5 size-2.5 rounded-full border-2 border-surface bg-success"
        />
      ) : null}
    </span>
  );
  return title ? <Tooltip label={name}>{body}</Tooltip> : body;
}

/** Overlapping avatars with a +N overflow. */
export function AvatarStack({
  users,
  max = 4,
  size = 'sm',
  className,
}: {
  users: { id: string; name: string; image?: string | null }[];
  max?: number;
  size?: AvatarSize;
  className?: string;
}) {
  const shown = users.slice(0, max);
  const extra = users.length - shown.length;
  return (
    <span className={cn('inline-flex items-center', className)}>
      {shown.map((u, i) => (
        <Avatar
          key={u.id}
          id={u.id}
          name={u.name}
          image={u.image}
          size={size}
          title
          className={cn('rounded-full ring-2 ring-surface', i > 0 && '-ml-1.5')}
        />
      ))}
      {extra > 0 ? (
        <Tooltip
          label={users
            .slice(max)
            .map((u) => u.name)
            .join(', ')}
        >
          <span
            className={cn(
              '-ml-1.5 inline-flex items-center justify-center rounded-full bg-hover font-semibold text-fg-muted ring-2 ring-surface',
              SIZES[size],
            )}
          >
            +{extra}
          </span>
        </Tooltip>
      ) : null}
    </span>
  );
}
